import type { Asset, AssetType } from "schemas/generated/asset";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { V1AssetsGrid } from "@v1/components/assets/AssetsGrid";
import { selectStoreDoesNotHaveReadFilesAccess } from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { AssetGridEmpty } from "@v2/features/assets/components/asset-grid/EmptyState";
import { NoSearchResults } from "@v2/features/assets/components/asset-grid/NoSearchResults";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { assetRouter } from "@v2/trpc/routes/asset";

const INITIAL_ASSETS_TO_LOAD = 16;

type ShopifyAssetsGridProps = {
  assetTypes?: AssetType[];
  searchTerm: string;
  onAssetSelected?: (asset: Asset) => void;
};

export const ShopifyAssetsGrid: React.FC<ShopifyAssetsGridProps> = ({
  assetTypes = ["image", "video"],
  searchTerm,
  onAssetSelected,
}) => {
  const workspaceId = useCurrentWorkspaceId();
  const projectId = useCurrentProjectId();
  const doesNotHaveReadFilesAccess = useEditorSelector(
    selectStoreDoesNotHaveReadFilesAccess,
  );
  const { data, isLoading, fetchNextPage, hasNextPage } =
    assetRouter.listShopify.useInfiniteQuery(
      workspaceId && projectId && !doesNotHaveReadFilesAccess
        ? {
            workspaceId,
            projectId,
            types: assetTypes,
            limit: INITIAL_ASSETS_TO_LOAD,
            search: searchTerm,
          }
        : skipToken,
      {
        getNextPageParam: ({ nextCursor }) => nextCursor,
      },
    );
  const assets = React.useMemo(
    () => data?.pages?.flatMap((page) => page.assets) ?? [],
    [data],
  );
  return (
    <V1AssetsGrid.Root
      assets={assets}
      searchTerm={searchTerm}
      isLoading={isLoading}
      hasNextPage={hasNextPage}
      fetchNextPage={() => void fetchNextPage()}
      emptyState={
        searchTerm ? (
          <NoSearchResults searchTerm={searchTerm} />
        ) : (
          <ShopifyAssetEmptyState />
        )
      }
      shouldAllowActions={false}
      onAssetSelected={onAssetSelected}
    >
      <V1AssetsGrid.ListDetailed />
    </V1AssetsGrid.Root>
  );
};

const ShopifyAssetEmptyState: React.FC = () => {
  return (
    <AssetGridEmpty
      title="No Shopify assets found"
      description="View your Shopify Files here, or upload through Replo for faster load times (recommended)."
    />
  );
};
